<template>
  <component
    :is="wrapperComponent"
    v-bind="wrapperProps"
    class="project-snippet"
    :class="{ 'px-48px py-24px': $media.mobile, 'p-32px': $media.tablet }"
  >
    <div class="project-snippet__head">
      <h3
        class="title is-size-5 has-text-weight-semibold"
        :class="{ 'mb-24px': $media.mobile, 'mb-48px': $media.tablet, 'is-5': $media.mobile, 'is-4': $media.tablet }"
      >
        {{ data.name }}
      </h3>
    </div>
    <div class="project-snippet__foot">
      <div :class="{ 'mb-24px': $media.mobile, 'mb-48px': $media.tablet }">
        <donut-chart :size="150" :sectors="sectors">
          <div class="is-size-3 has-text-black has-text-weight-normal">{{ donePercent }}%</div>
        </donut-chart>
      </div>
      <status-badge v-if="donePercent === 100" :badge="doneBadge" :hide-not-applicable="false" :show-label="true" />
      <project-status-badge v-else :status="data.status" style="border: 1px solid currentColor; background-color: transparent" />
    </div>
  </component>
</template>

<script>
import IndicatorHelper from '@/helpers/indicator-helper'
import { URL_PROJECT_COMPLIANCE } from '@/consts/urls'
import DonutChart from './donut-chart'
import StatusBadge from '@/components/indicator/status-badge'
import ProjectStatusBadge from '@/components/projects/project-status-badge'

export default {
  components: { DonutChart, StatusBadge, ProjectStatusBadge },
  props: {
    data: { type: Object, required: true },
    clickable: Boolean
  },
  computed: {
    wrapperComponent() {
      return this.clickable ? 'router-link' : 'div'
    },
    wrapperProps() {
      if (this.clickable) {
        return {
          to: { name: URL_PROJECT_COMPLIANCE, params: { projectId: this.data.id } }
        }
      }
      return null
    },

    // Counts and percentages
    todoCount() {
      return this.data.stats.directIndicatorsTodoCount
    },
    todoPercent() {
      if (!this.totalCount) return 0
      return Math.round((this.todoCount / this.totalCount) * 100)
    },
    inProgressCount() {
      return this.data.stats.directIndicatorsInProgressCount
    },
    inProgressPercent() {
      if (!this.totalCount) return 0
      return Math.round((this.inProgressCount / this.totalCount) * 100)
    },
    doneCount() {
      return this.data.stats.directIndicatorsCompletedCount
    },
    donePercent() {
      if (!this.totalCount) return 100
      return Math.round((this.doneCount / this.totalCount) * 100)
    },
    totalCount() {
      return this.doneCount + this.inProgressCount + this.todoCount
    },

    doneBadge() {
      const badgeDefinition = IndicatorHelper.badges().badgeDone
      return {
        ...badgeDefinition,
        color: 'success-dark'
      }
    },

    // Sectors
    sectors() {
      return [
        {
          value: this.donePercent,
          color: 'rgb(22, 131, 60)'
        },
        {
          value: this.inProgressPercent,
          color: 'rgb(31, 73, 234)'
        },
        {
          value: this.todoPercent,
          color: 'rgb(228, 150, 31)'
        }
      ]
    }
  }
}
</script>

<fragments>
fragment projectSnippet on ProjectGraphType {
  id
  name
  status
  stats {
    directIndicatorsCompletedCount
    directIndicatorsInProgressCount
    directIndicatorsTodoCount
  }
}
</fragments>

<style lang="scss">
.project-snippet {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  text-align: center;

  border: 2px solid $grey-light;
  border-radius: 6px;

  &__head,
  &__foot {
    display: block;
    flex: 0 1 auto;
    width: 100%;
    text-align: center;
  }

  // Hotfix for radial progress stroke ending
  svg circle {
    stroke-linecap: butt;
  }
}

a.project-snippet {
  &:hover,
  &:focus {
    .project-snippet {
      &__head {
        opacity: 0.7;
      }
      &__foot {
        opacity: 0.8;
      }
    }
  }
}
</style>
